/**
 * Mock bookmarks service.
 * Mirrors the real bookmarks.service.js API with in-memory data.
 */
import { mockDelay, mockLog } from './mock-delay';

// In-memory bookmarks store: Map of postId -> { postId, postType, createdAt }
let bookmarks = new Map([
  ['mock_post_w01', { postId: 'mock_post_w01', postType: 'workout', createdAt: '2026-03-20T10:00:00Z' }],
  ['mock_post_m02', { postId: 'mock_post_m02', postType: 'meal', createdAt: '2026-03-19T14:30:00Z' }],
  ['mock_post_t01', { postId: 'mock_post_t01', postType: 'text', createdAt: '2026-03-18T09:15:00Z' }],
]);

/**
 * Bookmark a post.
 */
export const bookmarkPost = async (userId, postId, postType = 'text') => {
  mockLog('BookmarksService', 'bookmarkPost', { userId, postId, postType });
  await mockDelay(100, 300);
  bookmarks.set(postId, {
    postId,
    postType,
    createdAt: new Date().toISOString(),
  });
};

/**
 * Remove a bookmark.
 */
export const unbookmarkPost = async (userId, postId) => {
  mockLog('BookmarksService', 'unbookmarkPost', { userId, postId });
  await mockDelay(100, 300);
  bookmarks.delete(postId);
};

/**
 * Get paginated bookmarks for a user.
 */
export const getBookmarks = async (userId, lastDoc = null, pageLimit = 20) => {
  mockLog('BookmarksService', 'getBookmarks', { userId, lastDoc, pageLimit });
  await mockDelay();

  const allBookmarks = Array.from(bookmarks.values()).sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  let startIndex = 0;
  if (lastDoc) {
    const idx = allBookmarks.findIndex((b) => b.postId === lastDoc);
    if (idx !== -1) startIndex = idx + 1;
  }

  const page = allBookmarks.slice(startIndex, startIndex + pageLimit);
  const newLastDoc = page.length > 0 ? page[page.length - 1].postId : null;
  const hasMore = startIndex + pageLimit < allBookmarks.length;

  return { bookmarks: page, lastDoc: newLastDoc, hasMore };
};

/**
 * Check if a single post is bookmarked.
 */
export const checkIfBookmarked = async (userId, postId) => {
  mockLog('BookmarksService', 'checkIfBookmarked', { userId, postId });
  await mockDelay(50, 150);
  return bookmarks.has(postId);
};

/**
 * Check bookmark status for multiple posts at once.
 */
export const checkBookmarkedBatch = async (userId, postIds) => {
  mockLog('BookmarksService', 'checkBookmarkedBatch', { userId, count: postIds?.length });
  await mockDelay(100, 250);
  const result = new Set();
  if (!postIds) return result;
  for (const postId of postIds) {
    if (bookmarks.has(postId)) result.add(postId);
  }
  return result;
};
